// A shelf device: the authored stage package (model, screen material roles,
// button and touch proxies) placed in the room at room scale, with canvases
// the emulator paints. It rests on whatever the room's anchor offers: a
// cradle it leans in, or a surface it lies on. Stands are room furniture.
//
// The package format and the material binding follow PocketJS's stage
// adapter (vendor/pocketjs/site/assets/pocket-stage-web.js): the profile is
// the only authority on model facts.

import * as THREE from "three";
import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";
import type { DeviceId } from "../core/types.ts";
import type { DeviceAnchor, Support } from "../room/contract.ts";
import { LAYER_CAST } from "./live-shadows.ts";
import type { Clearance } from "./clearance.ts";
import { clearTurn, route } from "./motion.ts";
import type { Pose } from "./rig.ts";
import { isWholly, marksIn, patchFor, withoutMarks } from "./marks.ts";
import { applySkin, PARTS } from "./skins.ts";
import { face } from "./type.ts";

export const LAYER_SELECT = 1;
export const LAYER_INPUT = 2;

export interface StagePart {
  name: string;
  center_mm: [number, number, number];
  half_extents_mm: [number, number, number];
  button?: string;
  touch_surface?: "primary" | "auxiliary";
  logical_size?: [number, number];
}

export interface StageProfile {
  name: string;
  lods: { settled: string; orbit: string };
  coordinate_system?: "authored_mm";
  target_width_mm: number;
  rotation_degrees?: [number, number, number];
  display: { logical_size: [number, number]; raster_density: number; auxiliary_size?: [number, number] };
  screen: { material_role: string; material_name_prefix: string; expected_primitives: number };
  suppressed_materials?: Array<{ material_role: string; material_name_prefix: string }>;
  parts?: StagePart[];
  hinge?: { node: string; default_angle_degrees: number; min_angle_degrees: number; max_angle_degrees: number };
}

/** A device's pose on one kind of support, relative to the anchor. */
interface Rest {
  rotation: THREE.Quaternion;
  offset: THREE.Vector3;
  /** Lid angle in degrees for a device with a hinge. */
  lid: number | null;
  /** Bounds of the resting device in the anchor's frame, metres. */
  box: THREE.Box3;
}

/** Hinge angles for a clamshell: propped open in a cradle, an open L on a surface. */
const LID = { cradle: 172, surface: 118 };

function monochrome(material: THREE.Material): THREE.Material {
  if (material.userData?.pocket3d_base_color_mode !== "monochrome") return material;
  material.onBeforeCompile = (shader) => {
    shader.fragmentShader = shader.fragmentShader.replace(
      "diffuseColor *= sampledDiffuseColor;",
      `diffuseColor *= sampledDiffuseColor;
       float pocketMonochrome = dot(sampledDiffuseColor.rgb, vec3(0.2126, 0.7152, 0.0722));
       diffuseColor.rgb = vec3(pocketMonochrome) * diffuse;`,
    );
  };
  material.customProgramCacheKey = () => "pocket-studio-monochrome";
  return material;
}

function screenTexture(canvas: HTMLCanvasElement): THREE.CanvasTexture {
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.flipY = false;
  texture.generateMipmaps = false;
  texture.minFilter = THREE.LinearFilter;
  texture.magFilter = THREE.LinearFilter;
  return texture;
}

export class DeviceActor {
  /** World placement: the anchor's point and yaw. */
  readonly root = new THREE.Group();
  private pose = new THREE.Group();
  private canon = new THREE.Group();
  /** Button and touch proxies in profile millimetres, raycast on LAYER_INPUT. */
  readonly proxies = new THREE.Group();
  /** One box around the whole device, raycast on LAYER_SELECT. */
  readonly hitbox: THREE.Mesh;
  readonly primary = document.createElement("canvas");
  readonly auxiliary: HTMLCanvasElement | null = null;
  private textures: THREE.CanvasTexture[] = [];
  private hinge: THREE.Object3D | null = null;
  private model: THREE.Object3D;
  /** Skinnable materials, cloned for this actor, with their authored colours. */
  readonly materials: THREE.MeshStandardMaterial[] = [];
  /** How the device sits on each kind of support it accepts. */
  private rests = new Map<Support, Rest>();
  /** The last answer of `showing`, kept while the device stays where it was. */
  private shown: { key: string; out: number; sway: number } | null = null;
  private rest!: Rest;
  /** Where the pose sits before anything holds it off: a rest's offset, or a blend of two in flight. */
  private base = new THREE.Vector3();
  /** How far the device is held off that: up, and out the way it faces. */
  private held = new THREE.Vector3();
  /** Bounds of the resting device in the root's frame, metres. */
  readonly box = new THREE.Box3();
  /** The model's own screen meshes, by the screen each shows: a screen that takes no touch has no proxy to find it by. */
  private screens: Array<{ mesh: THREE.Mesh; screen: "primary" | "auxiliary" }> = [];

  private constructor(readonly id: DeviceId, readonly profile: StageProfile, model: THREE.Object3D, scale: number, private label: string) {
    this.root.name = `device:${id}`;
    const [w, h] = profile.display.logical_size;
    const density = profile.display.raster_density;
    this.primary.width = w * density;
    this.primary.height = h * density;
    if (profile.display.auxiliary_size) {
      this.auxiliary = document.createElement("canvas");
      this.auxiliary.width = profile.display.auxiliary_size[0];
      this.auxiliary.height = profile.display.auxiliary_size[1];
    }

    this.model = this.canonicalize(model);
    this.bind(this.model);
    this.hinge = profile.hinge ? this.model.getObjectByName(profile.hinge.node) ?? null : null;

    for (const part of profile.parts ?? []) {
      if (!part.button && !part.touch_surface) continue;
      const [hx, hy, hz] = part.half_extents_mm;
      const geometry = part.touch_surface ? new THREE.PlaneGeometry(hx * 2, hy * 2) : new THREE.BoxGeometry(hx * 2, hy * 2, hz * 2);
      const proxy = new THREE.Mesh(geometry, new THREE.MeshBasicMaterial());
      proxy.layers.set(LAYER_INPUT);
      proxy.position.fromArray(part.center_mm);
      proxy.userData.part = part;
      this.proxies.add(proxy);
    }

    this.canon.scale.setScalar(0.001 * scale);
    this.canon.add(this.model, this.proxies);
    this.pose.add(this.canon);
    this.root.add(this.pose);

    this.hitbox = new THREE.Mesh(new THREE.BoxGeometry(1, 1, 1), new THREE.MeshBasicMaterial());
    this.hitbox.layers.set(LAYER_SELECT);
    this.hitbox.userData.device = this;
    this.root.add(this.hitbox);
    this.drawIdle("ready");
  }

  private setLid(degrees: number | null): void {
    if (this.hinge && degrees !== null) this.hinge.rotation.x = THREE.MathUtils.degToRad(180 - degrees);
  }

  /**
   * Work out how the device sits on a support. In a cradle its bottom back
   * edge goes to the anchor and it leans back by the cradle's tilt. On a
   * surface it lies face up, centred on the anchor.
   */
  private measure(support: Support, tilt: number): Rest {
    const lid = this.hinge ? LID[support] : null;
    this.setLid(lid);
    this.pose.position.set(0, 0, 0);
    this.pose.quaternion.identity();
    this.root.updateMatrixWorld(true);
    const upright = new THREE.Box3().setFromObject(this.model, true);
    const rotation = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(1, 0, 0), support === "cradle" ? -tilt : -Math.PI / 2);
    let offset: THREE.Vector3;
    if (support === "cradle") {
      const corner = new THREE.Vector3((upright.min.x + upright.max.x) / 2, upright.min.y, upright.min.z);
      offset = corner.applyQuaternion(rotation).negate();
    } else {
      this.pose.quaternion.copy(rotation);
      this.root.updateMatrixWorld(true);
      const lying = new THREE.Box3().setFromObject(this.model, true);
      offset = new THREE.Vector3(-(lying.min.x + lying.max.x) / 2, -lying.min.y, -(lying.min.z + lying.max.z) / 2);
    }
    this.pose.quaternion.copy(rotation);
    this.pose.position.copy(offset);
    this.root.updateMatrixWorld(true);
    return { rotation, offset, lid, box: new THREE.Box3().setFromObject(this.model, true) };
  }

  /** Measure the rests once, while the root is still at the origin. */
  private prepare(tilt: number, supports: readonly Support[]): void {
    for (const support of supports) this.rests.set(support, this.measure(support, tilt));
  }

  private restFor(anchor: DeviceAnchor): Rest {
    const rest = this.rests.get(anchor.support);
    if (!rest) throw new Error(`${this.id} cannot rest on a ${anchor.support}`);
    return rest;
  }

  /** Take the pose for a support at once. */
  private settle(rest: Rest): void {
    this.rest = rest;
    this.setLid(rest.lid);
    this.pose.quaternion.copy(rest.rotation);
    this.pose.position.copy(this.base.copy(rest.offset)).add(this.held);
    this.box.copy(rest.box);
    const size = rest.box.getSize(new THREE.Vector3());
    this.hitbox.scale.set(size.x * 1.08, size.y * 1.06, Math.max(size.z * 1.2, 0.05));
    this.hitbox.position.copy(rest.box.getCenter(new THREE.Vector3()));
  }

  /** Put the device on an anchor. */
  place(anchor: DeviceAnchor): void {
    this.root.position.fromArray(anchor.position);
    this.root.rotation.y = anchor.yaw;
    this.settle(this.restFor(anchor));
  }

  /**
   * A flight from where the device is to `anchor`: returns a function of
   * 0..1 that moves it along an arc and blends its pose and lid between the
   * two rests. At 1 the device is settled on the anchor.
   */
  flight(anchor: DeviceAnchor, clearance: Clearance | null): (t: number) => void {
    const from = { position: this.root.position.clone(), yaw: this.root.rotation.y, rest: this.rest };
    const to = this.restFor(anchor);
    const end = new THREE.Vector3().fromArray(anchor.position);
    // Between two kinds of rest the device changes shape: it tips up or
    // down and its lid moves.
    const same = from.rest === to;
    const measured = new THREE.Box3();
    const blend = (t: number) => {
      this.pose.quaternion.slerpQuaternions(from.rest.rotation, to.rotation, t);
      this.pose.position.copy(this.base.lerpVectors(from.rest.offset, to.offset, t)).add(this.held);
      if (from.rest.lid !== null && to.lid !== null) this.setLid(THREE.MathUtils.lerp(from.rest.lid, to.lid, t));
      if (same || t <= 0 || t >= 1) return;
      // Turning from one rest to the other swings part of the device below
      // the level it stands on at either end. Measure it and keep its lowest
      // point at the level the two rests blend to.
      this.root.updateMatrixWorld(true);
      const low = measured.setFromObject(this.model, true).min.y - this.root.position.y;
      const floor = THREE.MathUtils.lerp(from.rest.box.min.y, to.box.min.y, t) + this.held.y;
      if (low < floor) {
        this.base.y += floor - low;
        this.pose.position.y += floor - low;
      }
    };
    // Its bounds part of the way through, measured on the model itself with
    // the root at the origin, as the rests were.
    const boxAt = (t: number) => {
      const { position, rotation } = this.root;
      const [x, y, z, yaw] = [position.x, position.y, position.z, rotation.y];
      position.set(0, 0, 0);
      rotation.y = 0;
      blend(t);
      this.root.updateMatrixWorld(true);
      const box = new THREE.Box3().setFromObject(this.model, true);
      position.set(x, y, z);
      rotation.y = yaw;
      return box;
    };
    const path = route(
      { position: from.position, yaw: from.yaw, box: from.rest.box }, { position: end, yaw: anchor.yaw, box: to.box }, clearance,
      same ? undefined : boxAt,
    );
    blend(0);
    this.root.updateMatrixWorld(true);
    return (t) => {
      path(t, this.root.position);
      this.root.rotation.y = THREE.MathUtils.lerp(from.yaw, anchor.yaw, t);
      blend(t);
      if (t >= 1) this.settle(to);
    };
  }

  /**
   * Hold the device off its support: `up` metres, and `out` metres the way
   * it faces. The stand belongs to the room and stays.
   */
  hold(up: number, out: number): void {
    this.held.set(0, up, out);
    this.pose.position.copy(this.base).add(this.held);
  }

  /**
   * How a selected device may be shown where it stands: lifted by `up`, it
   * turns `sway` radians to either side. Returns how far out it must be
   * held for that turn to touch nothing it does not touch at rest, and the
   * turn itself, which is narrowed only when no distance within reach is
   * enough.
   */
  showing(up: number, clearance: Clearance | null): { out: number; sway: number } {
    const key = `${this.root.position.toArray().join()}|${this.root.rotation.y}|${up}`;
    if (this.shown?.key === key) return this.shown;
    const found = clearTurn({ position: this.root.position, yaw: this.root.rotation.y, box: this.rest.box }, up, clearance);
    this.shown = { key, ...found };
    return this.shown;
  }

  /**
   * `label` is the name the idle screen shows; `supports` the ways this
   * device may rest; `tilt` the lean of the room's cradles.
   */
  static async load(id: DeviceId, baseUrl: string, scale: number, label: string, supports: readonly Support[], tilt: number): Promise<DeviceActor> {
    const response = await fetch(baseUrl + "profile.json");
    if (!response.ok) throw new Error(`device profile for ${id} not found`);
    const profile = (await response.json()) as StageProfile;
    const gltf = await new GLTFLoader().loadAsync(baseUrl + profile.lods.settled);
    const actor = new DeviceActor(id, profile, gltf.scene, scale, label);
    actor.prepare(tilt, supports);
    return actor;
  }

  /** Orient the raw model so X is right, Y is up and the screen faces +Z, in millimetres. */
  private canonicalize(raw: THREE.Object3D): THREE.Object3D {
    const degrees = this.profile.rotation_degrees ?? [0, 0, 0];
    raw.rotation.set(
      THREE.MathUtils.degToRad(degrees[0]), THREE.MathUtils.degToRad(degrees[1]), THREE.MathUtils.degToRad(degrees[2]),
    );
    raw.updateMatrixWorld(true);
    if (this.profile.coordinate_system === "authored_mm") return raw;
    const oriented = new THREE.Box3().setFromObject(raw);
    const size = oriented.getSize(new THREE.Vector3());
    const centre = oriented.getCenter(new THREE.Vector3());
    raw.position.copy(centre).multiplyScalar(-1);
    const group = new THREE.Group();
    group.scale.setScalar(this.profile.target_width_mm / size.x);
    group.add(raw);
    group.updateMatrixWorld(true);
    return group;
  }

  /**
   * Give the screens their live textures, hide suppressed overlays, take out
   * the marks of other companies (marks.ts), clone the rest for skinning.
   */
  private bind(model: THREE.Object3D): void {
    const primary = screenTexture(this.primary);
    this.textures.push(primary);
    const auxiliary = this.auxiliary ? screenTexture(this.auxiliary) : null;
    if (auxiliary) this.textures.push(auxiliary);
    const suppressed = this.profile.suppressed_materials ?? [];
    const clones = new Map<THREE.Material, THREE.Material>();
    let screens = 0;

    const configure = (material: THREE.Material): THREE.Material => {
      const role = material.userData?.pocket3d_role as string | undefined;
      if (auxiliary && role === "dynamic_screen_auxiliary") {
        return new THREE.MeshBasicMaterial({ name: material.name, map: auxiliary, toneMapped: false });
      }
      if (role === this.profile.screen.material_role || material.name.startsWith(this.profile.screen.material_name_prefix)) {
        screens++;
        return new THREE.MeshBasicMaterial({ name: material.name, map: primary, side: THREE.DoubleSide, toneMapped: false });
      }
      if (suppressed.some((entry) => role === entry.material_role || material.name.startsWith(entry.material_name_prefix))) {
        return new THREE.MeshBasicMaterial({ name: material.name, transparent: true, opacity: 0, depthWrite: false, colorWrite: false });
      }
      let clone = clones.get(material);
      if (!clone) {
        clone = monochrome(material.clone());
        clone.userData = { ...material.userData, authored: (material as THREE.MeshStandardMaterial).color?.clone() };
        clones.set(material, clone);
        if ((clone as THREE.MeshStandardMaterial).isMeshStandardMaterial) this.materials.push(clone as THREE.MeshStandardMaterial);
      }
      return clone;
    };

    // Parts the model's author merged into a larger material get their own:
    // the mesh's triangles are put in groups by where they lie, each part's
    // first and the rest of the material last. A triangle belongs to the
    // first part that claims it.
    const parts = new Map<string, THREE.Material>();
    const split = (mesh: THREE.Mesh): void => {
      const whole = mesh.material as THREE.MeshStandardMaterial;
      const here = PARTS[this.id].filter((entry) => whole.name.startsWith(entry.material));
      if (here.length === 0 || !whole.isMeshStandardMaterial) return;
      const geometry = mesh.geometry;
      const position = geometry.getAttribute("position");
      const index = geometry.getIndex();
      const corners = index ? index.count : position.count;
      const claimed: number[][] = here.map(() => []);
      const rest: number[] = [];
      const centre = new THREE.Vector3();
      const corner = new THREE.Vector3();
      for (let i = 0; i < corners; i += 3) {
        const triangle = [0, 1, 2].map((k) => (index ? index.getX(i + k) : i + k));
        centre.set(0, 0, 0);
        for (const vertex of triangle) centre.add(corner.fromBufferAttribute(position, vertex));
        centre.multiplyScalar(1 / 3).applyMatrix4(mesh.matrixWorld);
        const at = here.findIndex((entry) => entry.where(centre));
        (at < 0 ? rest : claimed[at]!).push(...triangle);
      }
      if (claimed.every((corners) => corners.length === 0)) return;
      const order: number[] = [];
      const materials: THREE.Material[] = [];
      geometry.clearGroups();
      here.forEach((part, at) => {
        const corners = claimed[at]!;
        if (corners.length === 0) return;
        let own = parts.get(part.name);
        if (!own) {
          own = whole.clone();
          own.name = part.name;
          own.userData = { ...whole.userData };
          parts.set(part.name, own);
          this.materials.push(own as THREE.MeshStandardMaterial);
        }
        geometry.addGroup(order.length, corners.length, materials.length);
        order.push(...corners);
        materials.push(own);
      });
      if (rest.length > 0) {
        geometry.addGroup(order.length, rest.length, materials.length);
        order.push(...rest);
        materials.push(whole);
      }
      geometry.setIndex(order);
      mesh.material = materials.length === 1 ? materials[0]! : materials;
      if (materials.length === 1) geometry.clearGroups();
    };

    // A mark of another company leaves the model here. One that is a whole
    // material goes with its mesh; one drawn among legends loses its
    // triangles; one engraved in the shell loses its grooves and is closed
    // with a patch.
    const gone: THREE.Object3D[] = [];
    const patches: Array<[THREE.Mesh, THREE.Mesh]> = [];
    const unmark = (mesh: THREE.Mesh, name: string): boolean => {
      const marks = marksIn(this.id, name);
      if (marks.length === 0) return true;
      if (isWholly(marks)) {
        gone.push(mesh);
        return false;
      }
      const { kept, taken } = withoutMarks(mesh.geometry, mesh.matrixWorld, marks);
      if (taken.size > 0) mesh.geometry.setIndex(kept);
      for (const mark of marks) {
        if (!mark.engraved) continue;
        const { patch, fit } = patchFor(mesh.geometry, mesh.matrixWorld, mark);
        if (fit > 0.12) throw new Error(`${this.id}: the shell around "${mark.name}" is ${fit.toFixed(2)} mm off a smooth surface`);
        // The patch is fitted in the device's frame; it hangs from the shell's mesh.
        patch.applyMatrix4(mesh.matrixWorld.clone().invert());
        const cover = new THREE.Mesh(patch, mesh.material);
        cover.name = `patch:${mark.name}`;
        cover.layers.mask = mesh.layers.mask;
        cover.receiveShadow = true;
        patches.push([mesh, cover]);
      }
      return true;
    };

    model.traverse((object) => {
      const mesh = object as THREE.Mesh;
      if (!mesh.isMesh) return;
      const name = Array.isArray(mesh.material) ? "" : mesh.material.name;
      mesh.material = Array.isArray(mesh.material) ? mesh.material.map(configure) : configure(mesh.material);
      const shows = (Array.isArray(mesh.material) ? mesh.material : [mesh.material]).map((material) => (material as THREE.MeshBasicMaterial).map);
      if (shows.includes(primary)) this.screens.push({ mesh, screen: "primary" });
      else if (auxiliary && shows.includes(auxiliary)) this.screens.push({ mesh, screen: "auxiliary" });
      if (!unmark(mesh, name)) return;
      if (!Array.isArray(mesh.material)) split(mesh);
      mesh.layers.enable(LAYER_CAST);
      mesh.castShadow = true;
      mesh.receiveShadow = true;
    });
    for (const mesh of gone) mesh.removeFromParent();
    for (const [mesh, cover] of patches) mesh.add(cover);
    if (screens !== this.profile.screen.expected_primitives) {
      throw new Error(`${this.id}: screen matched ${screens} primitives, expected ${this.profile.screen.expected_primitives}`);
    }
  }

  /** The screen a ray meets first, or null. The ray is cast on the model's own layer (0). */
  screenAt(raycaster: THREE.Raycaster): "primary" | "auxiliary" | null {
    const hit = raycaster.intersectObjects(this.screens.map((entry) => entry.mesh), false)[0];
    return hit ? this.screens.find((entry) => entry.mesh === hit.object)!.screen : null;
  }

  /** Mark the screen textures dirty after the emulator painted. */
  refreshScreens(): void {
    for (const texture of this.textures) texture.needsUpdate = true;
  }

  /** Paint the screens without a running app: dark glass, or the ready card with a caption. */
  drawIdle(kind: "off" | "ready", title = this.label): void {
    const paint = (canvas: HTMLCanvasElement, primary: boolean) => {
      const g = canvas.getContext("2d")!;
      const { width, height } = canvas;
      if (kind === "off") {
        const sheen = g.createLinearGradient(0, 0, width, height);
        sheen.addColorStop(0, "#15121f");
        sheen.addColorStop(0.5, "#0a0810");
        sheen.addColorStop(1, "#110e1a");
        g.fillStyle = sheen;
        g.fillRect(0, 0, width, height);
        return;
      }
      g.fillStyle = "#171226";
      g.fillRect(0, 0, width, height);
      if (!primary) return;
      const unit = Math.min(width, height) / 10;
      const cx = width / 2;
      const cy = height / 2 - unit * 0.6;
      g.lineWidth = unit * 0.32;
      g.strokeStyle = "#ffd23f";
      g.beginPath();
      g.roundRect(cx - unit * 1.75, cy - unit * 1.25, unit * 3.5, unit * 2.5, unit * 0.75);
      g.stroke();
      g.fillStyle = "#ff5f9e";
      g.beginPath();
      g.arc(cx - unit * 0.75, cy, unit * 0.39, 0, Math.PI * 2);
      g.fill();
      g.fillStyle = "#3fd0e8";
      g.beginPath();
      g.roundRect(cx, cy - unit * 0.42, unit * 1.25, unit * 0.28, unit * 0.14);
      g.fill();
      g.fillStyle = "#ff5f9e";
      g.beginPath();
      g.roundRect(cx, cy + unit * 0.15, unit * 0.8, unit * 0.28, unit * 0.14);
      g.fill();
      g.fillStyle = "#fcf6ff";
      g.font = face(700, unit * 0.9);
      g.textAlign = "center";
      g.textBaseline = "middle";
      g.fillText(title, cx, cy + unit * 2.6);
    };
    paint(this.primary, true);
    if (this.auxiliary) paint(this.auxiliary, false);
    this.refreshScreens();
  }

  /** Wear a catalog skin, or the member's own colour under CUSTOM_SKIN. */
  setSkin(skin: string, custom?: string | null): void {
    applySkin(this.id, skin, this.materials, custom);
  }

  /** The camera pose that faces this device, looking along its screen's normal, if it stood on `anchor`. */
  playPose(anchor: DeviceAnchor, aspect: number): Pose {
    const rest = this.restFor(anchor);
    const place = new THREE.Matrix4().compose(
      new THREE.Vector3().fromArray(anchor.position),
      new THREE.Quaternion().setFromAxisAngle(THREE.Object3D.DEFAULT_UP, anchor.yaw),
      new THREE.Vector3(1, 1, 1),
    );
    const centre = rest.box.getCenter(new THREE.Vector3()).applyMatrix4(place);
    const size = rest.box.getSize(new THREE.Vector3());
    const normal = new THREE.Vector3(0, 0, 1).applyQuaternion(rest.rotation).transformDirection(place);
    const fit = new THREE.Vector2(size.x * 1.5, Math.hypot(size.y, size.z) * 1.34);
    const distance = Math.max(fit.y / 2, fit.x / (2 * aspect)) / Math.tan(THREE.MathUtils.degToRad(14));
    return { position: centre.clone().addScaledVector(normal, distance), target: centre, fit };
  }

  /** World position a label or menu should point at: just above the device. */
  headPoint(): THREE.Vector3 {
    this.root.updateMatrixWorld(true);
    return this.root.localToWorld(new THREE.Vector3(0, this.box.max.y, this.box.getCenter(new THREE.Vector3()).z));
  }

  dispose(): void {
    for (const texture of this.textures) texture.dispose();
    this.root.traverse((object) => {
      const mesh = object as THREE.Mesh;
      if (mesh.isMesh) mesh.geometry.dispose();
    });
  }
}
